import React from 'react'

// Kendo components
import { Field, FieldWrapper } from '@progress/kendo-react-form'
import { Label } from '@progress/kendo-react-labels'
import { Checkbox, Input, TextArea } from '@progress/kendo-react-inputs'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { DatePicker } from '@progress/kendo-react-dateinputs'

import styles from '.././EDIForms.module.scss'

export const CustomShorterInput = (props: any) => {
    const { value, onChange } = props
    return (
        <Input
            type={props.inputType}
            style={{ width: '75px' }}
            maxLength={props.maxChar}
            value={value}
            onChange={onChange}
            required={props.required}
            disabled={props.disabled}
        />
    )
}

export const CustomShortInput = (props: any) => {
    const { value, onChange } = props
    return (
        <Input
            type={props.inputType}
            style={{ width: '200px' }}
            maxLength={props.maxChar}
            value={value}
            onChange={onChange}
            required={props.required}
            disabled={props.disabled}
        />
    )
}

export const CustomMediumInput = (props: any) => {
    const { value, onChange } = props
    return (
        <Input
            type={props.inputType}
            style={{ width: '300px', minWidth: '300px' }}
            maxLength={props.maxChar}
            value={value}
            onChange={onChange}
            required={props.required}
            disabled={props.disabled}
        />
    )
}

export const CustomLongInput = (props: any) => {
    const { value, onChange } = props
    return (
        <Input
            type={props.inputType}
            style={{ width: '500px', minWidth: '300px' }}
            maxLength={props.maxChar}
            value={value}
            onChange={onChange}
            required={props.required}
            disabled={props.disabled}
        />
    )
}

export const FormInput: React.FC<{
    label: string
    name: string
    length: string
    maxChar: number
    disabled?: boolean
    inputType?: string
    required?: boolean
    onChange?: (event: any) => void
}> = (props) => {
    return (
        <FieldWrapper>
            <Label>
                {props.required && (
                    <div className={styles.bolded}>
                        {props.label}
                        <span className={styles.required}>*</span>
                    </div>
                )}
                {!props.required && <div className={styles.bolded}>{props.label}</div>}
            </Label>
            {props.length === 'shorter' && (
                <Field
                    name={props.name}
                    component={CustomShorterInput}
                    maxChar={props.maxChar}
                    required={props.required}
                    disabled={props.disabled}
                    onChange={props.onChange}
                />
            )}
            {props.length === 'short' && (
                <Field
                    name={props.name}
                    component={CustomShortInput}
                    maxChar={props.maxChar}
                    required={props.required}
                    disabled={props.disabled}
                    onChange={props.onChange}
                />
            )}
            {props.length === 'medium' && (
                <Field
                    name={props.name}
                    component={CustomMediumInput}
                    maxChar={props.maxChar}
                    required={props.required}
                    disabled={props.disabled}
                    onChange={props.onChange}
                />
            )}
            {props.length === 'long' && (
                <Field
                    name={props.name}
                    component={CustomLongInput}
                    maxChar={props.maxChar}
                    required={props.required}
                    disabled={props.disabled}
                    onChange={props.onChange}
                />
            )}
        </FieldWrapper>
    )
}

export const CustomDate = (props: any) => {
    return (
        <DatePicker
            defaultValue={props.dateAsDate}
            format="yyyy-MM-dd hh:mm:ss a"
            defaultShow={false}
            onChange={props.onDateChange}
            required={true}
        />
    )
}

export const FormDate: React.FC<{
    label: string
    name: string
    required?: boolean
    dateAsDate: Date
    onDateChange: (event: any) => void
}> = (props) => {
    return (
        <FieldWrapper>
            <Label>
                {props.required && (
                    <div className={styles.bolded}>
                        {props.label}
                        <span className={styles.required}>*</span>
                    </div>
                )}
                {!props.required && <div className={styles.bolded}>{props.label}</div>}
            </Label>
            <Field
                name={props.name}
                component={CustomDate}
                dateAsDate={props.dateAsDate}
                onDateChange={props.onDateChange}
            />
        </FieldWrapper>
    )
}

export const CustomCheckbox = (props: any) => {
    const { value, onChange } = props
    return <Checkbox value={value} onChange={onChange} />
}

export const FormCheckbox: React.FC<{
    label: string
    name: string
    onChange?: (event: any) => void
}> = (props) => {
    return (
        <FieldWrapper>
            <Label>
                <div className={styles.bolded}>{props.label}</div>
            </Label>
            <Field name={props.name} component={CustomCheckbox} onChange={props.onChange} />
        </FieldWrapper>
    )
}

export const CustomDropDownList = (props: any) => {
    const { protocolList, value, onChange } = props
    return (
        <DropDownList
            data={protocolList}
            value={value}
            onChange={onChange}
            defaultValue={'Select Protocol...'}
            style={{ width: 200 }}
        />
    )
}

export const FormDropDownList: React.FC<{
    label: string
    name: string
    onChange?: (event: any) => void
}> = (props) => {
    const protocolList = ['AS2', 'CXML', 'FTP', 'SFTP', 'FTPS-Implicit', 'XCBL']

    return (
        <FieldWrapper>
            <Label editorId={props.label}>
                <div className={styles.bolded}>{props.label}</div>
            </Label>
            <Field
                name={props.name}
                component={CustomDropDownList}
                protocolList={protocolList}
                onChange={props.onChange}
            />
        </FieldWrapper>
    )
}

export const CustomTextArea = (props: any) => {
    const { value, onChange } = props
    return (
        <TextArea
            value={value ? value : undefined}
            onChange={onChange}
            style={{ height: '150px' }}
            maxLength={props.maxChar}
        />
    )
}

export const FormTextArea: React.FC<{
    label: string
    name: string
    maxChar: number
    onChange?: (event: any) => void
}> = (props) => {
    return (
        <FieldWrapper>
            <Label>
                <div className={styles.bolded}>{props.label}</div>
            </Label>
            <Field
                name={props.name}
                component={CustomTextArea}
                maxChar={props.maxChar}
                onChange={props.onChange}
            />
        </FieldWrapper>
    )
}

export const CustomRadioButton = (props: any) => {
    const { protocolList, value, onChange } = props
    return (
        <DropDownList
            data={protocolList}
            value={value}
            onChange={onChange}
            defaultValue={'Select Protocol...'}
        />
    )
}

export const FormRadio: React.FC<{
    name: string
    radioButtons: string[]
    label: string
}> = (props) => {
    return (
        <FieldWrapper>
            <Label>
                <div className={styles.bolded}>{props.label}</div>
            </Label>
            <Field
                name={props.name}
                component={CustomRadioButton}
                radioButtons={props.radioButtons}
            />
        </FieldWrapper>
    )
}
